import { useSetDeviceId } from "@/hooks/use-client-storage";
import { useDisconnection } from "@/hooks/use-disconnection";
import { CommonLocale } from "@/locales/localeid";
import { AppView } from "@/store/app/state";
import { CalendarFilter } from "@/store/teacher/state";
import { Paths } from "@/utils/paths";
import { Spin } from "ant-design-vue";
import { computed, defineComponent, watch } from "vue";
import { AuthService, LoginInfo, MainLayout, RoleName, WrongTenantRedirectModal, fmtMsg } from "vue-glcommonui";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { DynamicModal, SettingTeamsModal, LostConnectionWarning, CookieConsentBanner } from "@/components/common";
import { AppFooter, AppHeader } from "@/components/layout";
import { PARENT_PATH_REGEX, TEACHER_CALENDAR_PATH_REGEX, TEACHER_PATH_REGEX, TEACHER_SCHEDULE_INFO_PATH_REGEX } from "@/utils/constant";
import { useTabLock } from "@/hooks/use-tab-lock";
import { isChromeBrowser, isDesktopBrowser, isEdgeBrowser, isOperaBrowser, isYandexBrowser } from "@/utils/utils";
import BlockedBrowser from "@/views/blocked-browser/blocked-browser.vue";
import { MODULES, vuexName } from "@/store/utils";

export default defineComponent({
  components: {
    MainLayout,
    AppHeader,
    AppFooter,
    Spin,
    DynamicModal,
    SettingTeamsModal,
    LostConnectionWarning,
    CookieConsentBanner,
    BlockedBrowser,
    WrongTenantRedirectModal,
  },
  created() {
    AuthService.localSilentLogin();
  },
  setup() {
    const { getters, dispatch, commit } = useStore();
    useTabLock();
    const router = useRouter();
    const route = useRoute();
    useSetDeviceId();
    useDisconnection();
    const isHeaderVisible = computed(() => getters.appLayout !== "full");
    const isFooterVisible = computed(() => getters.appLayout !== "full");
    const loginInfo = computed(() => getters[vuexName(MODULES.AUTH.GETTERS.GET_LOGIN_INFO)]);
    const isSignedIn = computed(() => getters[vuexName(MODULES.AUTH.GETTERS.GET_IS_LOGGED_IN)]);
    const appView = computed(() => getters[vuexName(MODULES.APP.GETTERS.GET_APP_VIEW, false)]);
    const siteTitle = computed(() => fmtMsg(CommonLocale.CommonSiteTitle));
    const appPath = computed(() => route.path);
    const calendarFilter = computed<CalendarFilter>(() => getters[vuexName(MODULES.TEACHER.GETTERS.GET_CALENDAR_FILTER)]);
    const isSupportedBrowser = computed(() => {
      return isChromeBrowser() || isOperaBrowser() || isEdgeBrowser() || isYandexBrowser();
    });
    const isBrowserBlocked = computed(() => !isDesktopBrowser || !isSupportedBrowser.value);

    const onTeacherSignedIn = async (loginInfo: LoginInfo) => {
      await dispatch(vuexName(MODULES.TEACHER.DISPATCHES.SET_INFO), {
        id: loginInfo.profile.sub,
        name: loginInfo.profile.name,
      });
    };

    const onParentSignedIn = async (loginInfo: LoginInfo) => {
      await dispatch(vuexName(MODULES.PARENT.DISPATCHES.SET_INFO), {
        id: loginInfo.profile.sub,
        name: loginInfo.profile.name,
      });
    };

    const onUserSignedIn = async () => {
      const loginInfo: LoginInfo = getters[vuexName(MODULES.AUTH.GETTERS.GET_LOGIN_INFO)];
      const isTeacher: boolean = getters[vuexName(MODULES.AUTH.GETTERS.GET_IS_TEACHER)];
      const isParent: boolean = getters[vuexName(MODULES.AUTH.GETTERS.GET_IS_PARENT)];
      if (isTeacher) await onTeacherSignedIn(loginInfo);
      if (isParent) {
        await onParentSignedIn(loginInfo);
        await checkPolicy("parent");
      }
    };

    const checkPolicy = async (role: "parent" | "teacher") => {
      if (role !== "parent") return;

      const policyAccepted = getters[vuexName(MODULES.PARENT.GETTERS.GET_ACCEPT_POLICY)];
      if (!policyAccepted && location.pathname?.includes("student")) {
        await router.push({
          path: Paths.Parent,
          query: { target: location.pathname },
        });
      }
    };

    watch(isSignedIn, async () => {
      if (isSignedIn.value) onUserSignedIn();
    });

    watch(loginInfo, (currentLoginInfo: LoginInfo | null) => {
      if (!currentLoginInfo || typeof currentLoginInfo.profile?.remoteTsiSettings === "undefined") return;
      const isTeacher = currentLoginInfo.profile.roles.indexOf(RoleName.teacher) !== -1 && currentLoginInfo.profile.remoteTsiSettings;
      const isParent = currentLoginInfo.profile.roles.indexOf(RoleName.parent) !== -1;
      const { pathname } = window.location;
      if ((!isParent && !isTeacher) || (isParent && isTeacher)) {
        return;
      }
      if (isTeacher) {
        const matchIndex = pathname.search(PARENT_PATH_REGEX);
        if (matchIndex > -1) {
          location.pathname = pathname.replace(PARENT_PATH_REGEX, "/teacher");
        }
      }
      if (isParent) {
        const matchIndex = pathname.search(TEACHER_PATH_REGEX);
        if (matchIndex > -1) {
          location.pathname = pathname.replace(TEACHER_PATH_REGEX, "/parent");
        }
      }
    });

    watch(appPath, async () => {
      window.scrollTo(0, 0);
      if (calendarFilter.value.classId || calendarFilter.value.groupId || calendarFilter.value.date || calendarFilter.value.isShowWeekends) {
        const calenderPathMatchIndex = appPath.value.search(TEACHER_CALENDAR_PATH_REGEX);
        const scheduleInfoPathMatchIndex = appPath.value.search(TEACHER_SCHEDULE_INFO_PATH_REGEX);
        if (calenderPathMatchIndex < 0 && scheduleInfoPathMatchIndex < 0) {
          commit(vuexName(MODULES.TEACHER.COMMITS.SET_CALENDAR_FILTER), {
            classId: "",
            groupId: "",
            date: "",
            isShowWeekends: false,
          });
        }
      }
    });

    const isMaskGrandAccess = computed(() => getters[vuexName(MODULES.SPIN.GETTERS.GET_MASK_GRAND_ACCESS)]);

    const versionUpdatedText = "V12.4 • June 28, 2026";

    return {
      siteTitle,
      appView,
      AppView,
      isSignedIn,
      isHeaderVisible,
      isFooterVisible,
      isMaskGrandAccess,
      isBrowserBlocked,
      isDesktopBrowser,
      versionUpdatedText,
    };
  },
});
